import { match, matchPath } from 'react-router'
import { createSelector } from 'reselect'
import { selectBudgetRequestListModelsFromState } from 'src/app/BudgetsRequests/redux/budgetRequest.selectors'
import { creditCardOption } from 'src/components/CreditCardDropdown/CreditCardDropdown'
import { userEmailOption } from 'src/components/UserDropdown/UserDropdown'
import { UserListModel } from 'src/models/user.models'
import {
  UpdateBudgetRequestMatchParams,
  updateBudgetRequestRouteProps,
} from 'src/navigation/routeProps'
import { RootState } from 'src/redux/createRootReducer'
import { CreditCardsFullPaginatedDataState } from 'src/redux/creditCardsFullPaginatedData.reducer'
import { RecentUsersFullPaginatedDataState } from 'src/redux/recentUsersFullPaginatedData.reducer'
import { selectUserIdFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectCreditCardListModelsFromState,
  selectCreditCardPermissionsModelsFromState,
} from 'src/redux/selectors/data/creditCards.selectors'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { selectLocationFromState } from 'src/redux/selectors/router.selectors'
import { UsersFullPaginatedDataState } from 'src/redux/usersFullPaginatedData.reducer'
import { ErrorDetailMap } from 'src/utils/error.utils'

export const selectCreditCardIdFromRoute = createSelector(
  [selectLocationFromState],
  (location) => {
    const mtch = matchPath<UpdateBudgetRequestMatchParams>(
      location.pathname,
      updateBudgetRequestRouteProps,
    ) as match<UpdateBudgetRequestMatchParams>
    return mtch.params.id
  },
)

export const selectBudgetRequest = createSelector(
  [selectBudgetRequestListModelsFromState, selectCreditCardIdFromRoute],
  (creditCards, creditCardId) => {
    return creditCards[creditCardId] !== undefined
      ? creditCards[creditCardId]
      : undefined
  },
)

export const selectCreditCardPermissions = createSelector(
  [selectCreditCardPermissionsModelsFromState, selectCreditCardIdFromRoute],
  (creditCardPermissions, creditCardId) => {
    return creditCardPermissions[creditCardId] !== undefined
      ? creditCardPermissions[creditCardId]
      : undefined
  },
)

export const selectBudgetRecipient = createSelector(
  [selectBudgetRequest, selectUserListModelsFromState],
  (budget) => {
    if (budget !== undefined) {
      return new UserListModel(budget.requester)
    }
  },
)

export const selectBudgetApprover = createSelector(
  [selectBudgetRequest, selectUserListModelsFromState],
  (budget) => {
    if (budget !== undefined) {
      return new UserListModel(budget.approver)
    }
  },
)

export const selectPuttingCreditCardFromState = (state: RootState): boolean =>
  state.app.updateBudget.puttingCreditCard

export const selectTopLevelErrorFromState = (
  state: RootState,
): string | undefined => state.app.updateBudget.topLevelError

export const selectErrorDetailMapFromState = (
  state: RootState,
): ErrorDetailMap<string> | undefined => state.app.updateBudget.errorDetailMap

export const selectRecentUsersFullPaginatedData = (
  state: RootState,
): RecentUsersFullPaginatedDataState =>
  state.app.updateBudget.recentUsersFullPaginatedData

const selectUniqueRecentUserIds = createSelector(
  [selectRecentUsersFullPaginatedData],
  (data) => data.ids.filter((id, i) => data.ids.indexOf(id) === i),
)
const selectUniqueRecentUserEmailOptions = createSelector(
  [selectUniqueRecentUserIds, selectUserListModelsFromState],
  (ids, users) => ids.map((id) => userEmailOption(users[id])),
)

export const selectFilteredRecentUserEmailOptions = createSelector(
  [selectUniqueRecentUserEmailOptions, selectUserIdFromState],
  (options, ownUserId) => {
    const ownUser = options.find(
      (option) => option.details?.user.id === ownUserId,
    )
    const filteredOptions = options.filter(
      (option) => option.details?.user.id !== ownUserId,
    )
    if (ownUser) {
      filteredOptions.unshift(ownUser)
    }
    return filteredOptions.slice(0, 5)
  },
)

export const selectUsersFullPaginatedData = (
  state: RootState,
): UsersFullPaginatedDataState => state.app.budgets.usersFullPaginatedData
const selectUniqueUserIds = createSelector(
  [selectUsersFullPaginatedData],
  (data) => data.ids.filter((id, i) => data.ids.indexOf(id) === i),
)
const selectUniqueUserEmailOptions = createSelector(
  [selectUniqueUserIds, selectUserListModelsFromState],
  (ids, users) => ids.map((id) => userEmailOption(users[id])),
)
export const selectFilteredUserEmailOptions = createSelector(
  [selectUniqueUserEmailOptions, selectFilteredRecentUserEmailOptions],
  (options, suggested) =>
    options.filter(
      (option) =>
        suggested
          .map((suggestion) => suggestion.value)
          .indexOf(option.value) === -1,
    ),
)

export const selectCreditCardsFullPaginatedData = (
  state: RootState,
): CreditCardsFullPaginatedDataState =>
  state.app.budgets.creditCardsFullPaginatedData
const selectUniqueCreditCardIds = createSelector(
  [selectCreditCardsFullPaginatedData],
  (data) => data.ids.filter((id, i) => data.ids.indexOf(id) === i),
)
const selectUniqueCreditCardOptions = createSelector(
  [selectUniqueCreditCardIds, selectCreditCardListModelsFromState],
  (ids, creditCards) => ids.map((id) => creditCardOption(creditCards[id])),
)
export const selectFilteredCreditCardOptions = createSelector(
  [selectUniqueCreditCardOptions],
  (options) =>
    options.filter((option) => !option.details?.creditCard?.isBudget),
)
